import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  Avatar,
  Menu,
  MenuItem,
  Link,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { Notification } from "./Notification";
import { useAppProvider } from "../AppContext";
import {
  WalletConnector,
  ConnectedAccount,
  OKXWallet,
  UnisatWallet,
  SafnectWallet,
} from "../types";
import { formatUnisatNetwork, getAddressType } from "../utils";

const Connector = forwardRef<WalletConnector, {}>((props, ref) => {
  const {
    setNetwork,
    connectedAccount,
    setConnectedAccount,
    connectedWallet,
    setConnectedWallet,
  } = useAppProvider();

  useImperativeHandle(ref, (): WalletConnector => ({}));

  const [_openDialog, setOpenDialog] = useState(false);

  const [_notificationSeverity, setNotificationSeverity] = useState<
    "info" | "error" | "warning" | "success"
  >("info");
  const [_notificationMsg, setNotificationMsg] = useState<string | undefined>();

  const onDialogClose = () => {
    setOpenDialog(false);
  };

  const [_installedWallets, setInstalledWallets] = useState<string[]>([]);
  const detectInstalledWallets = async () => {
    const installedWallets: string[] = [];

    // detect unisat
    if (typeof window.unisat !== "undefined") {
      installedWallets.push(UnisatWallet);
    }

    // detect safnect

    if (typeof window.safnectwallet !== "undefined") {
      installedWallets.push(SafnectWallet);
    }

    setInstalledWallets(installedWallets);

    console.log("installed wallets", installedWallets);
    return installedWallets;
  };

  const detectConnectedWallet = async (installedWallets: string[]) => {
    setConnectedWallet(undefined);
    setConnectedAccount(undefined);

    // detect unisat connection
    if (installedWallets.includes(UnisatWallet)) {
      try {
        const accounts = await window.unisat.getAccounts();
        onUnisatAccountsChanged(accounts, false);
        return;
      } catch (e) {}
    }

    // detect safnectwallet connection
    if (installedWallets.includes(SafnectWallet)) {
      try {
        const results =
          await window.safnectwallet.fractalBitcoin.requestAccounts();
        onSafnectAccountsChanged(results.data);
        return;
      } catch (e) {
        console.log("connected safnectwallet accounts err", e);
      }
    }

    // detect okx connection
    if (installedWallets.includes(OKXWallet)) {
      try {
        const accounts: string[] =
          await window.okxwallet.fractalBitcoin.getAccounts();
        const pubkey = await window.okxwallet.fractalBitcoin.getPublicKey();
        if (accounts[0] && pubkey) {
          onOKXAccountChanged({
            address: accounts[0],
            publicKey: pubkey,
          });
        }
      } catch (e) {
        console.log("connected okx accounts err", e);
      }
    }
  };

  useEffect(() => {
    const handler = () => {
      detectInstalledWallets().then((installedWallets) => {
        return detectConnectedWallet(installedWallets);
      });
    };

    window.addEventListener("load", handler);

    return () => {
      window.removeEventListener("load", handler);

      if (_installedWallets.includes(UnisatWallet)) {
        window.unisat.removeListener(
          "accountsChanged",
          onUnisatAccountsChanged
        );
      }

      if (_installedWallets.includes(OKXWallet)) {
        window.okxwallet.fractalBitcoin.removeListener(
          "accountChanged",
          onOKXAccountChanged
        );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkWalletInstallation = (walletName: string) => {
    if (!_installedWallets.includes(walletName)) {
      if (typeof (window as any)[walletName] !== "undefined") {
        return true;
      }

      setNotificationMsg(
        `${walletName} Wallet not found. Please install it first.`
      );
      setNotificationSeverity("error");
      return false;
    }
    return true;
  };

  const [_connectingWallet, setConnectingWallet] = useState<
    string | undefined
  >();

  const onUnisatAccountsChanged = async (accounts: string[], notify = true) => {
    if (accounts.length > 0) {
      // update network according to the wallet
      try {
        const walletNetwork = formatUnisatNetwork(await window.unisat.getChain());
        const pubkey = await window.unisat.getPublicKey();
        if (pubkey) {
          setNetwork(walletNetwork);
          setConnectedWallet(UnisatWallet);
          setConnectedAccount({
            payment: {
              address: accounts[0],
              addressType: getAddressType(accounts[0], walletNetwork),
              publicKey: pubkey,
            },
          });
          window.unisat.on("accountsChanged", onUnisatAccountsChanged);
        }  
      } catch (e: any) {
        if (notify) {
          setNotificationMsg(`Unisat Connect Error: ${e.message}`);
          setNotificationSeverity("error");
        } else {
          console.error("Unisat Connect Error", e);
        }
      }
    } else {
      setConnectedWallet(undefined);
      setConnectedAccount(undefined);
    }
  };

  const onSafnectAccountsChanged = async (account?: {
    address: string;
    publicKey: string;
  }) => {
    if (account) {
      setNetwork("fractal-mainnet");
      setConnectedWallet(SafnectWallet);
      setConnectedAccount({
        payment: {
          address: account.address,
          addressType: getAddressType(account.address, "fractal-mainnet"),
          publicKey: account.publicKey,
        },
      });
    } else {
      setConnectedWallet(undefined);
      setConnectedAccount(undefined);
    }
  };

  const onOKXAccountChanged = async (account?: {
    address: string;
    publicKey: string;
  }) => {
    if (account) {
      const walletNetwork = "fractal-mainnet"; //okx wallet only mainet
      setNetwork(walletNetwork);
      setConnectedWallet(OKXWallet);
      setConnectedAccount({
        payment: {
          address: account.address,
          addressType: getAddressType(account.address, walletNetwork),
          publicKey: account.publicKey,
        },
      });
      window.okxwallet.fractalBitcoin.on("accountChanged", onOKXAccountChanged);
    } else {
      setConnectedWallet(undefined);
      setConnectedAccount(undefined);
    }
  };

  const onConnectSafnect = async () => {
    if (!checkWalletInstallation(SafnectWallet)) {
      return;
    }
    setOpenDialog(false);
    setConnectingWallet(SafnectWallet);

    try {
      let results = await window.safnectwallet.fractalBitcoin.requestAccounts();

      if (results.success === false) {
        const res = await window.safnectwallet.fractalBitcoin.connect();
        if (res.success === false) {
          throw new Error(res.msg);
        }
        results = await window.safnectwallet.fractalBitcoin.requestAccounts();
      }
      console.log("requestAccounts", results);
      onSafnectAccountsChanged(results.data);
    } catch (e: any) {
      console.error("connect safnectwallet err", e);
      setNotificationMsg(`Connect Error: ${e.message}`);
      setNotificationSeverity("error");
    }

    setConnectingWallet(undefined);
  };

  const onConnectUnisat = async () => {
    if (!checkWalletInstallation(UnisatWallet)) {
      return;
    }
    setOpenDialog(false);
    setConnectingWallet(UnisatWallet);

    try {
      const accounts = await window.unisat.requestAccounts();
      onUnisatAccountsChanged(accounts);
    } catch (e: any) {
      setNotificationMsg(`Connect Error: ${e.message}`);
      setNotificationSeverity("error");
    }

    setConnectingWallet(undefined);
  };

  // const onConnectOKX = async () => {
  //   console.log('onConnectOKX');
  //   if (!checkWalletInstallation(OKXWallet)) {
  //     console.log('onConnectOKX');
  //     return;
  //   }
  //   setOpenDialog(false);
  //   setConnectingWallet(OKXWallet);

  //   try {
  //     console.log('onConnectOKX connect');
  //     const result = await window.okxwallet.fractalBitcoin.connect();
  //     onOKXAccountChanged(
  //       result?.address && result?.publicKey
  //         ? {
  //             address: result.address,
  //             publicKey: result.publicKey,
  //           }
  //         : undefined
  //     );
  //   } catch (e: any) {
  //     setNotificationMsg(`Connect Error: ${e.message}`);
  //     setNotificationSeverity("error");
  //   }
  //   setConnectingWallet(undefined);
  // };

  const [_menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const disconnect = () => {
    setConnectedWallet(undefined);
    setConnectedAccount(undefined);
  };

  const condenseAddress = (account: ConnectedAccount) => {
    const address = account.payment.address;
    return `${address.slice(0, 6)}...${address.slice(-6)}`;
  };

  let walletIcon = `unisat.svg`;
  if (connectedWallet === SafnectWallet) {
    walletIcon = `safnect.png`;
  } else if (connectedWallet === OKXWallet) {
    walletIcon = `okx.svg`;
  }

  return (
    <Box>
      {connectedWallet && connectedAccount ? (
        <Box>
          <Button
            variant="outlined"
            startIcon={
              <Avatar
                variant="rounded"
                alt="wallet logo"
                src={`/${walletIcon}`}
                sx={{ mr: 1, width: 20, height: 20 }}
              />
            }
            onClick={(e) => {
              setMenuAnchorEl(e.currentTarget);
            }}
          >
            {condenseAddress(connectedAccount)}
          </Button>
          <Menu
            anchorEl={_menuAnchorEl}
            open={Boolean(_menuAnchorEl)}
            onClose={() => {
              setMenuAnchorEl(null);
            }}
            sx={{ zIndex: 1200 }}
          >
            <MenuItem
              onClick={() => {
                setMenuAnchorEl(null);
                disconnect();
              }}
              sx={{ color: "#FE9C2F" }}
            >
              <LinkOffIcon />
              <Link component="button" underline="none" sx={{ pl: 2 }}>
                Disconnect Wallet
              </Link>
            </MenuItem>
          </Menu>
        </Box>
      ) : (
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<AccountBalanceWalletIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Connect
        </Button>
      )}

      <Dialog onClose={onDialogClose} open={_openDialog}>
        <DialogTitle sx={{ textAlign: "center", mb: 2 }}>
          Connect Wallet
        </DialogTitle>
        <List sx={{}}>
          <ListItem>
            <ListItemButton
              autoFocus
              style={{ border: "1px solid #999", borderRadius: "3px" }}
              sx={{ pt: 3, pb: 3, pl: 5, pr: 5 }}
              onClick={onConnectSafnect}
            >
              <Avatar
                variant="square"
                alt="Safnect wallet logo"
                src="/safnect.png"
                sx={{ mr: 2 }}
              />
              <Typography variant="h3">Safnect Wallet</Typography>
            </ListItemButton>
          </ListItem>
          <ListItem>
            <ListItemButton
              autoFocus
              style={{ border: "1px solid #999", borderRadius: "3px" }}
              sx={{ pt: 3, pb: 3, pl: 5, pr: 5 }}
              onClick={onConnectUnisat}
              disabled={
                !_installedWallets.includes(UnisatWallet) ||
                _connectingWallet === UnisatWallet
              }
            >
              <Avatar
                variant="square"
                alt="unisat wallet logo"
                src="/unisat.svg"
                sx={{ mr: 2 }}
              />
              <Typography variant="h3">Unisat Wallet</Typography>
            </ListItemButton>
          </ListItem>
        </List>
      </Dialog>
      <Notification
        message={_notificationMsg}
        setMessage={setNotificationMsg}
        severity={_notificationSeverity}
      />
    </Box>
  );
});

export default Connector;
